import { API_BASE_URL } from '../config/api';

export interface FoodResponse {
  id: string;
  name: string;
  category: string;
  calories: number;
  protein: number;
  fat: number;
  carbohydrates: number;
  fiber?: number;
  description?: string;
  standardUnit?: string; // Standart birim: "adet", "dilim", "porsiyon", "su bardağı" vb.
  standardUnitWeight?: number; // Standart birimin gram cinsinden ağırlığı
  createdAt: Date;
  updatedAt?: Date;
}

export interface CreateFoodRequest {
  name: string;
  category: string;
  calories: number;
  protein: number;
  fat: number;
  carbohydrates: number;
  fiber?: number;
  description?: string;
}

export interface UpdateFoodRequest {
  name?: string;
  category?: string;
  calories?: number;
  protein?: number;
  fat?: number;
  carbohydrates?: number;
  fiber?: number;
  description?: string;
}

// Tüm besinleri getir
export const getAllFoods = async (): Promise<FoodResponse[]> => {
  const response = await fetch(`${API_BASE_URL}/foods`);
  if (!response.ok) {
    throw new Error('Besinler getirilemedi');
  }
  return response.json();
};

// ID'ye göre besin getir
export const getFoodById = async (id: string): Promise<FoodResponse> => {
  const response = await fetch(`${API_BASE_URL}/foods/${id}`);
  if (!response.ok) {
    throw new Error('Besin bulunamadı');
  }
  return response.json();
};

// Besin ara
export const searchFoods = async (
  searchTerm?: string,
  category?: string
): Promise<FoodResponse[]> => {
  const params = new URLSearchParams();
  if (searchTerm) params.append('searchTerm', searchTerm);
  if (category) params.append('category', category);

  const response = await fetch(
    `${API_BASE_URL}/foods/search?${params.toString()}`
  );
  if (!response.ok) {
    throw new Error('Besin araması başarısız');
  }
  return response.json();
};

// Kategorileri getir
export const getCategories = async (): Promise<string[]> => {
  const response = await fetch(`${API_BASE_URL}/foods/categories`);
  if (!response.ok) {
    throw new Error('Kategoriler getirilemedi');
  }
  return response.json();
};

// Yeni besin ekle
export const createFood = async (
  food: CreateFoodRequest
): Promise<FoodResponse> => {
  const response = await fetch(`${API_BASE_URL}/foods`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(food),
  });

  if (!response.ok) {
    throw new Error('Besin eklenemedi');
  }
  return response.json();
};

// Besin güncelle
export const updateFood = async (
  id: string,
  food: UpdateFoodRequest
): Promise<FoodResponse> => {
  const response = await fetch(`${API_BASE_URL}/foods/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(food),
  });

  if (!response.ok) {
    throw new Error('Besin güncellenemedi');
  }
  return response.json();
};

// Besin sil
export const deleteFood = async (id: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/foods/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Besin silinemedi');
  }
};

// Besin bazlı standart birim dönüşüm tablosu (gram cinsinden)
// Her besin için en yaygın birimlerin ağırlıkları
const foodUnitConversions: Record<string, Record<string, number>> = {
  // Yumurtalar
  'Tavuk Yumurta (Tam)': { adet: 65, gram: 1 },
  'Tavuk Yumurta (Beyaz)': { adet: 33, gram: 1 }, // Ortalama yumurta beyazı
  'Tavuk Yumurta (Sarı)': { adet: 17, gram: 1 }, // Ortalama yumurta sarısı
  'Bıldırcın Yumurta': { adet: 11, gram: 1 },
  'Ördek Yumurta': { adet: 70, gram: 1 },
  'Kaz Yumurta': { adet: 160, gram: 1 },
  
  // Meyveler
  'Elma': { adet: 150, gram: 1 },
  'Muz': { adet: 120, gram: 1 },
  'Portakal': { adet: 150, gram: 1 },
  'Mandalina': { adet: 100, gram: 1 },
  'Şeftali': { adet: 130, gram: 1 },
  'Armut': { adet: 175, gram: 1 },
  'Üzüm': { salkım: 120, gram: 1 },
  'Çilek': { adet: 12, gram: 1 }, // 1 çilek ≈ 12g
  'Kiraz': { adet: 8, gram: 1 }, // 1 kiraz ≈ 8g
  
  // Ekmek ve Tahıllar
  'Ekmek (Beyaz)': { dilim: 28, gram: 1 },
  'Ekmek (Tam Buğday)': { dilim: 28, gram: 1 },
  'Ekmek (Kepekli)': { dilim: 28, gram: 1 },
  'Ekmek (Çavdar)': { dilim: 28, gram: 1 },
  'Baget': { dilim: 22, gram: 1 },
  'Bulgur (Haşlanmış)': { porsiyon: 150, gram: 1 },
  'Pirinç (Haşlanmış)': { porsiyon: 150, gram: 1 },
  'Makarna': { porsiyon: 150, gram: 1 },
  'Makarna (Haşlanmış)': { porsiyon: 150, gram: 1 },
  
  // Süt ve Süt Ürünleri
  'Beyaz Peynir': { dilim: 25, gram: 1 },
  'Kaşar Peyniri': { dilim: 25, gram: 1 },
  'Labne': { 'yemek kaşığı': 18, gram: 1 },
  'Yoğurt': { kase: 175, gram: 1 },
  'Süt': { 'su bardağı': 200, ml: 1, gram: 1 },
  'Ayran': { 'su bardağı': 200, ml: 1, gram: 1 },
  
  // Et ve Et Ürünleri
  'Tavuk Göğsü': { porsiyon: 175, gram: 1 },
  'Tavuk But': { porsiyon: 100, gram: 1 },
  'Köfte (Izgara)': { adet: 40, gram: 1 },
  'Sucuk': { dilim: 20, gram: 1 },
  'Salam': { dilim: 12, gram: 1 },
  'Pastırma': { dilim: 15, gram: 1 },
  
  // Sebzeler
  'Domates': { adet: 125, gram: 1 },
  'Salatalık': { adet: 125, gram: 1 },
  'Biber': { adet: 75, gram: 1 },
  'Patlıcan': { adet: 250, gram: 1 },
  'Kabak': { adet: 200, gram: 1 },
  'Havuç': { adet: 80, gram: 1 },
  'Soğan': { adet: 100, gram: 1 },
  
  // İçecekler
  'Su': { 'su bardağı': 200, ml: 1, gram: 1 },
  'Çay': { fincan: 150, ml: 1, gram: 1 },
  'Kahve': { fincan: 125, ml: 1, gram: 1 },
};

// Miktar ve birime göre besin değerlerini hesapla
export const calculateNutritionFromFood = (
  food: FoodResponse,
  quantity: number,
  unit: string = 'gram'
): {
  calories: number;
  protein: number;
  fat: number;
  carbohydrates: number;
  fiber: number;
} => {
  let multiplier: number;

  // Gram veya ml girişi (doğrudan)
  if (unit === 'gram' || unit === 'ml') {
    multiplier = quantity / 100;
  } 
  // Öncelik 1: Veritabanından gelen standart birim bilgisi (en doğru)
  else if (food.standardUnit === unit && food.standardUnitWeight) {
    multiplier = (quantity * food.standardUnitWeight) / 100;
  }
  // Öncelik 2: Frontend'deki besin bazlı dönüşüm tablosu (fallback)
  else if (foodUnitConversions[food.name] && foodUnitConversions[food.name][unit]) {
    const unitWeight = foodUnitConversions[food.name][unit];
    multiplier = (quantity * unitWeight) / 100;
  }
  // Fallback: Genel tahminler (eski sistem - geriye dönük uyumluluk)
  else {
    const fallbackMultipliers: Record<string, number> = {
      porsiyon: 1.5, // 1 porsiyon ≈ 150g (genel tahmin)
      adet: 0.5, // 1 adet ≈ 50g (genel tahmin - yanlış olabilir!)
      'su bardağı': 2, // 1 su bardağı ≈ 200ml
      dilim: 0.25, // 1 dilim ≈ 25g (genel tahmin)
      kase: 1.75, // 1 kase ≈ 175g (genel tahmin)
      fincan: 1.25, // 1 fincan ≈ 125ml (genel tahmin)
    };
    const fallbackMultiplier = fallbackMultipliers[unit] || 1;
    multiplier = (quantity * fallbackMultiplier) / 100;
    
    // Uyarı: Fallback kullanıldığında console'a log (geliştirme için)
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `⚠️ "${food.name}" için "${unit}" birimi tanımlı değil. Genel tahmin kullanılıyor.`
      );
    }
  }

  return {
    calories: Math.round(food.calories * multiplier),
    protein: Math.round(food.protein * multiplier * 10) / 10,
    fat: Math.round(food.fat * multiplier * 10) / 10,
    carbohydrates: Math.round(food.carbohydrates * multiplier * 10) / 10,
    fiber: Math.round((food.fiber || 0) * multiplier * 10) / 10,
  };
};
